<template>
  <Teleport to="body">
    <div v-if="mediaReady" class="intro-overlay">

      <!-- Видео.
           poster + opacity:0 до первого кадра — иначе WebView секунду рисует свою
           серую заглушку с треугольником поверх чёрного вейла (вейл ниже по z-index). -->
      <video
        v-if="mediaType === 'video'"
        ref="videoRef"
        class="intro-media intro-media--video"
        :class="{ 'is-visible': videoVisible }"
        :src="mediaUrl"
        :poster="BLANK_POSTER"
        autoplay
        playsinline
        muted
        preload="auto"
        disablepictureinpicture
        disableremoteplayback
        @playing="onVideoPlaying"
        @ended="hide"
        @error="hide"
      />

      <!-- Картинка с прогресс-баром таймера -->
      <template v-else-if="mediaType === 'image'">
        <img
          class="intro-media"
          :src="mediaUrl"
          alt=""
          @error="hide"
        />
        <div class="intro-progress">
          <div class="intro-progress-bar" :style="{ animationDuration: IMAGE_DURATION_MS + 'ms' }" />
        </div>
      </template>

    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, nextTick } from 'vue';
import { Capacitor } from '@capacitor/core';
import { consola } from 'consola';

declare global {
  interface Window {
    __removeIntroVeil?: (onDone?: () => void) => void;
    // Ставится boot-скриптом (scripts/intro-boot.js), инлайненным в <head>
    __intro?: {
      /** Медиа нашлось и играет */
      active: boolean;
      /** Ролик доиграл (или его не было) — вызывается сразу, если уже завершилось */
      onFinish: (cb: () => void) => void;
    };
  }
}

// Сколько секунд показывать изображение
const IMAGE_DURATION_MS = 4000;

// Чёрный 1x1 постер. Без него WebView рисует собственную серую заглушку
// (большой треугольник + круглая кнопка play) пока не готов первый кадр.
const BLANK_POSTER = `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='1' height='1'%3E%3Crect width='1' height='1' fill='%23000'/%3E%3C/svg%3E`;

// Порядок поиска файлов: сначала видео, потом картинки.
// Не писать литералы с расширениями напрямую в src — Vite/Rollup может трактовать как import
const CANDIDATES: Array<{ ext: string; type: 'video' | 'image' }> = [
  { ext: 'mp4',  type: 'video' },
  { ext: 'webm', type: 'video' },
  { ext: 'jpg',  type: 'image' },
  { ext: 'jpeg', type: 'image' },
  { ext: 'png',  type: 'image' },
  { ext: 'webp', type: 'image' },
];

const mediaReady = ref(false);
const mediaUrl   = ref('');
const mediaType  = ref<'video' | 'image' | null>(null);
const videoRef   = ref<HTMLVideoElement | null>(null);
// Видео показываем только когда реально отрисован первый кадр
const videoVisible = ref(false);

let imageTimer: ReturnType<typeof setTimeout> | null = null;
let forceHideTimer: ReturnType<typeof setTimeout> | null = null;

const forceRemoveVeilElement = () => {
  const veil = document.querySelector('#cap-intro-veil');
  if (veil?.parentNode) {
    veil.remove();
  }
};

const removeVeil = (onDone?: () => void) => {
  if (typeof window.__removeIntroVeil === 'function') {
    window.__removeIntroVeil(onDone);
  } else {
    onDone?.();
  }
  // Fallback: если глобальный helper не сработал/не был создан, удаляем элемент напрямую
  setTimeout(() => {
    forceRemoveVeilElement();
  }, 450);
};

const hide = () => {
  if (imageTimer) { clearTimeout(imageTimer); imageTimer = null; }
  if (forceHideTimer) { clearTimeout(forceHideTimer); forceHideTimer = null; }
  videoRef.value?.pause();
  videoVisible.value = false;
  mediaReady.value = false;
  removeVeil();
};

// Первый кадр отрисован — можно показывать. requestVideoFrameCallback точнее
// события `playing` (оно стреляет до попадания кадра на экран), но есть не везде.
const onVideoPlaying = () => {
  const v = videoRef.value;
  if (!v) return;

  const reveal = () => { videoVisible.value = true; };
  const rvfc = (v as HTMLVideoElement & {
    requestVideoFrameCallback?: (cb: () => void) => number;
  }).requestVideoFrameCallback;

  if (typeof rvfc === 'function') {
    rvfc.call(v, reveal);
    // Подстраховка: если колбэк не придёт (фон/подавленный рендер) — не остаёмся в черноте
    setTimeout(reveal, 400);
  } else {
    reveal();
  }
};

// Предзагрузка видео — ждём canplay/loadeddata (достаточно данных для начала воспроизведения).
// ВАЖНО: на iOS WKWebView не-присоединённый к DOM <video> часто не начинает буферизацию,
// поэтому временно вставляем элемент в DOM (1x1, прозрачный) на время предзагрузки.
const preloadVideo = (url: string): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    const v = document.createElement('video');
    v.src = url;
    v.muted = true;
    v.playsInline = true;
    v.setAttribute('playsinline', '');
    v.setAttribute('muted', '');
    v.preload = 'auto';
    v.style.cssText = 'position:fixed;left:-9999px;width:1px;height:1px;opacity:0;pointer-events:none;';
    let settled = false;
    const finish = (ok: boolean, err?: Error) => {
      if (settled) return;
      settled = true;
      v.remove();
      if (ok) resolve();
      else reject(err ?? new Error('video-error'));
    };
    v.addEventListener('canplay', () => finish(true));
    v.addEventListener('loadeddata', () => finish(true));
    v.addEventListener('error', () => finish(false, new Error('video-error')));
    setTimeout(() => finish(false, new Error('video-timeout')), 3000);
    document.body.append(v);
    v.load();
  });

// Предзагрузка картинки — ждём onload
const preloadImage = (url: string): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    const img = new Image();
    let done = false;
    img.addEventListener('load', () => { done = true; resolve(); });
    img.addEventListener('error', () => { done = true; reject(new Error('image-error')); });
    setTimeout(() => { if (!done) reject(new Error('image-timeout')); }, 3000);
    img.src = url;
  });

const logger = consola.withTag('intro');
const log = (...a: unknown[]) => logger.info(...a);

onMounted(async () => {
  if (!Capacitor.isNativePlatform()) {
    log('skip: not native platform');
    return;
  }
  log('start, platform =', Capacitor.getPlatform());

  // Штатный путь: интро уже играет — его запустил boot-скрипт (scripts/intro-boot.js)
  // задолго до гидратации Vue. Нам остаётся только снять вейл, когда ролик доиграл,
  // потому что «приложение готово» знает именно этот компонент, а не boot-скрипт.
  const boot = window.__intro;
  if (boot) {
    log('boot-скрипт владеет интро, active =', boot.active);
    // Страховка на случай, если ролик завис и boot-скрипт не позвал onFinish
    forceHideTimer = setTimeout(() => { log('forceHide (failsafe)'); hide(); }, 12_000);
    boot.onFinish(() => {
      log('интро завершено → removeVeil');
      if (forceHideTimer) { clearTimeout(forceHideTimer); forceHideTimer = null; }
      removeVeil();
    });
    return;
  }

  // Фолбэк для старых нативных оболочек, где boot-скрипта в бандле ещё нет.
  log('boot-скрипта нет, ищем медиа сами');

  // Глобальный страховочный таймаут: если что-то зависло — всё равно убираем вейл
  const globalTimeout = setTimeout(() => { log('globalTimeout → removeVeil'); removeVeil(); }, 4000);
  // Жесткий fail-safe: если видео так и не закончилось/зависло — закрываем оверлей
  forceHideTimer = setTimeout(() => { log('forceHide (failsafe)'); hide(); }, 12_000);

  try {
    for (const { ext, type } of CANDIDATES) {
      const url = '/intro.' + ext;

      try {
        // Не используем HEAD-проверку: в схеме capacitor:// она ненадёжна.
        // Пробуем сразу загрузить — если файла нет, preload отклонится по onerror.
        log('trying', url);
        if (type === 'video') {
          await preloadVideo(url);
        } else {
          await preloadImage(url);
        }
        log('preloaded OK', url);

        clearTimeout(globalTimeout);
        mediaUrl.value  = url;
        mediaType.value = type;
        mediaReady.value = true;

        // Ждём nextTick + небольшую паузу чтобы браузер успел отрисовать первый кадр
        await nextTick();
        // Подстраховка для iOS: атрибут `muted` во Vue не всегда выставляет JS-свойство,
        // а WKWebView блокирует автоплей НЕ-muted видео. Поэтому выставляем явно и дёргаем play().
        if (type === 'video' && videoRef.value) {
          videoRef.value.muted = true;
          videoRef.value.defaultMuted = true;
          videoRef.value.setAttribute('muted', '');
          try { await videoRef.value.play(); }
          catch (error) {
            // Автоплей не стартовал — показывать нечего. Раньше оверлей висел
            // до failsafe (12с) с нативной заглушкой; теперь просто уходим.
            log('video.play() rejected', String(error));
            hide();
            return;
          }
        }
        await new Promise<void>(r => setTimeout(r, 300));
        log('overlay shown, removing veil');
        removeVeil();

        // Для картинки запускаем таймер автозакрытия
        if (type === 'image') {
          imageTimer = setTimeout(hide, IMAGE_DURATION_MS);
        }

        return; // нашли и показали — выходим из цикла
      } catch (error) {
        // Этот файл не загрузился — пробуем следующий
        log('preload failed', url, String(error));
        continue;
      }
    }

    // Ни один файл не найден — убираем вейл
    log('no media matched → removeVeil');
    clearTimeout(globalTimeout);
    removeVeil();
  } catch (error) {
    log('error', String(error));
    clearTimeout(globalTimeout);
    removeVeil();
  }
});

onUnmounted(() => {
  if (imageTimer) { clearTimeout(imageTimer); imageTimer = null; }
  if (forceHideTimer) { clearTimeout(forceHideTimer); forceHideTimer = null; }
  removeVeil();
});
</script>

<style scoped>
.intro-overlay {
  position: fixed;
  inset: 0;
  /* Должен быть ВЫШЕ стартового вейла #cap-intro-veil (z-index:99999 из nuxt.config),
     иначе видео рисуется под чёрной заглушкой и его не видно. */
  z-index: 100000;
  background: #000;
  overflow: hidden;
}

.intro-media {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center center;
}

/* До первого кадра держим видео прозрачным — видно чёрный фон оверлея,
   а не серую заглушку WebView. */
.intro-media--video {
  opacity: 0;
  transition: opacity 120ms linear;
}

.intro-media--video.is-visible {
  opacity: 1;
}

/* Нативная кнопка play (та самая белая «таблетка») и панель контролов.
   Появляются, когда автоплей ещё не стартовал — нам они не нужны никогда. */
.intro-media--video::-webkit-media-controls,
.intro-media--video::-webkit-media-controls-panel,
.intro-media--video::-webkit-media-controls-start-playback-button,
.intro-media--video::-webkit-media-controls-overlay-play-button {
  display: none !important;
  -webkit-appearance: none;
  appearance: none;
}

/* Прогресс-бар для картинок */
.intro-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: rgba(255, 255, 255, 0.2);
  z-index: 2;
}

.intro-progress-bar {
  height: 100%;
  width: 100%;
  background: rgba(255, 255, 255, 0.8);
  transform-origin: left;
  animation: progress-shrink linear forwards;
  animation-delay: 500ms; /* стартует после появления картинки */
}

@keyframes progress-shrink {
  from { transform: scaleX(1); }
  to   { transform: scaleX(0); }
}

</style>
